import { Box, Link, Stack, Tile, Typography, useTheme } from "@carrotfertility/carotene-core"
import React, { useContext, useEffect, useState } from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useWatch } from "@carrotfertility/carotene-core-x"
import { CarrotCardCostSharePreAuthContext } from "#/components/carrot-card-cost-share/state/CarrotCardCostSharePreAuthContext"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"
import { JourneySubTypeValues } from "#/components/carrot-card-cost-share/steps/shared/stepOptionValues"
import { DbgPaths } from "#/utils/Paths"
import { Link as RouterLink } from "react-router-dom"

export function CarrotRxRequiredBox({ name }: { name: string }): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const value = useWatch({ name })
  const {
    serverState: { isCarrotRxRequired, hasFertilityCareRxCoverage, hasPreservationRxCoverage },
    formState: { journeyType, journeySubType }
  } = useContext(CarrotCardCostSharePreAuthContext)
  const [showCarrotRxRequired, setShowCarrotRxRequired] = useState(false)

  useEffect(() => {
    const shouldShowCarrotRxRequired = (() => {
      if (value === JourneySubTypeValues.MEDICATIONS) {
        return isCarrotRxRequired
      }

      if (value === "YES") {
        if (journeyType === JourneyTypes.FERTILITY_CARE) {
          return isCarrotRxRequired && hasFertilityCareRxCoverage
        } else if (journeyType === JourneyTypes.PRESERVATION) {
          return isCarrotRxRequired && hasPreservationRxCoverage
        } else if (journeyType === JourneyTypes.GC) {
          return isCarrotRxRequired
        }
      }

      return false
    })()

    setShowCarrotRxRequired(shouldShowCarrotRxRequired)
  }, [isCarrotRxRequired, hasFertilityCareRxCoverage, hasPreservationRxCoverage, journeyType, value, journeySubType])

  return showCarrotRxRequired ? (
    <Tile>
      <Stack direction="row" spacing={theme.tokens.spacing.md} alignItems="flex-start">
        <Box
          component="img"
          src={"/images/carrot-rx-circle.svg"}
          alt={intl.formatMessage({ defaultMessage: "Carrot Rx" })}
          sx={{
            inlineSize: "40px",
            blockSize: "40px",
            flexShrink: 0,
            borderRadius: "50%",
            border: (theme) => `9px solid ${theme.palette.background.hover}`
          }}
        />
        <Box>
          <Typography variant="h5">
            <FormattedMessage defaultMessage="Carrot Rx required" />
          </Typography>
          <Typography sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
            <FormattedMessage
              defaultMessage="All medications must be filled through <link>Carrot Rx</link> to be eligible."
              values={{
                link: (linkContent) => (
                  <Link
                    component={RouterLink}
                    to={DbgPaths.PHARMACY}
                    color="inherit"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
        </Box>
      </Stack>
    </Tile>
  ) : null
}
